import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { dealLabel } from './lib/dealLabel';

export interface Deal {
  id: string;
  title: string;
  jobNumber: string | null;
  stage: string;
  amountCents: number;
}

interface DealPickerProps {
  deals: Deal[];
  value: string | null;
  onChange: (dealId: string) => void;
}

const MAX_RESULTS = 50;

export function DealPicker({ deals, value, onChange }: DealPickerProps): JSX.Element {
  const [query, setQuery] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const deferredQuery = useDeferredValue(query);

  const selectedDeal = useMemo(
    () => deals.find((d) => d.id === value) ?? null,
    [deals, value],
  );

  const filtered = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    if (q === '') return deals.slice(0, MAX_RESULTS);
    const matches: Deal[] = [];
    for (const d of deals) {
      const haystack = `${d.jobNumber ?? ''} ${d.title}`.toLowerCase();
      if (haystack.includes(q)) {
        matches.push(d);
        if (matches.length >= MAX_RESULTS) break;
      }
    }
    return matches;
  }, [deals, deferredQuery]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = (event: MouseEvent): void => {
      const node = wrapperRef.current;
      if (!node) return;
      if (event.target instanceof Node && !node.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isOpen]);

  const handleSelect = (dealId: string): void => {
    onChange(dealId);
    setQuery('');
    setIsOpen(false);
  };

  const placeholder = selectedDeal ? dealLabel(selectedDeal) : 'Search by job number or title…';

  return (
    <div ref={wrapperRef} className="relative flex flex-col gap-1">
      <input
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        aria-label="Search deals"
        autoComplete="off"
        className="min-h-[44px] rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal outline-none ring-1 ring-brand-steel placeholder:text-brand-slate/60 focus:ring-2 focus:ring-brand-blue"
      />
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-72 overflow-y-auto rounded-lg bg-brand-white ring-1 ring-brand-steel shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-sm text-brand-slate">No matches</div>
          ) : (
            <ul className="flex flex-col">
              {filtered.map((d) => {
                const isSelected = d.id === value;
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(d.id)}
                      className={`min-h-[44px] w-full px-3 py-2 text-left text-sm text-brand-charcoal hover:bg-brand-steel focus:bg-brand-steel focus:outline-none ${
                        isSelected ? 'bg-brand-steel' : ''
                      }`}
                    >
                      {dealLabel(d)}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
